import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Link,
  TextField,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Joi, { ValidationError } from 'joi'
import React from 'react'
import { StepComponentProps } from 'react-step-builder'
import config from '../../config'
import stepIcon from '../../images/step-illustration-profile.png'
import analytics from '../../libs/analytics'
import InputAdornmentSVGWrapper from './common/InputAdornmentSVGWrapper'
import Navigation from './common/Navigation'
import OnboardStepper from './common/OnboardStepper'
import {
  handleCheckboxChanges,
  handleTextAreaChanges,
  handlePasswordConfirmationChanges,
  setFieldTouched,
} from './common/formHelpers'
import FormFieldPassword from './components/FormFieldPassword'

class StepProfile extends React.PureComponent<StepComponentProps> {
  state: {
    formErrors: { [key: string]: ValidationError | undefined }
    touchedFields: Set<string>
    formValidated: boolean
  } = {
    formErrors: {},
    touchedFields: new Set(),
    formValidated: false,
  }

  render() {
    const { formErrors, touchedFields, formValidated } = this.state
    return (
      <Grid container direction="column">
        <Grid item sx={{ mb: { xs: 3, sm: 4, md: 4 } }}>
          <OnboardStepper activeStep={1} />
        </Grid>
        <Grid container item direction="row" spacing={5} justifyContent="spaceBetween">
          <Grid item xs={12} sm={12} md={7}>
            <Box>
              <Typography variant="h1">Set up your profile</Typography>
              {/* <Typography variant="subtitle1">Your login details will let you access your company dashboard</Typography> */}
            </Box>
            <Box
              component="form"
              sx={{
                mt: 1,
                mb: 3,
                '& > :not(style)': { mt: 3, width: '100%', maxWidth: 500 },
                display: 'flex',
                flexDirection: 'column',
              }}
              noValidate
              autoComplete="off"
            >
              <TextField
                autoFocus={!this.props.getState('full_name', '')}
                required
                id="full_name"
                type="text"
                placeholder="Full name"
                variant="outlined"
                value={this.props.getState('full_name', '')}
                onChange={(e) => handleTextAreaChanges(e, this)}
                onBlur={(e) => setFieldTouched(e, this)}
                error={!!formErrors.full_name && (touchedFields.has('full_name') || formValidated)}
                helperText={(touchedFields.has('full_name') || formValidated) && formErrors.full_name?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InputAdornmentSVGWrapper>
                        <path
                          d="M17 0C7.61175 0 0 7.61175 0 17C0 26.3882 7.61175 34 17 34C26.3882 34 34 26.3882 34 17C34 7.61175 26.3882 0 17 0ZM7.08333 27.1164C8.73658 22.1581 13.6949 24.0933 14.5208 21.4072V19.6166C14.1497 19.4168 13.0857 18.0455 12.9738 16.9745C12.6806 16.949 12.2216 16.6586 12.0856 15.5097C12.0133 14.8934 12.3023 14.5463 12.4766 14.4372C12.4766 14.4372 12.0402 13.4427 12.0402 12.4553C12.0417 9.52 13.4952 7.08333 17 7.08333C18.8927 7.08333 19.4792 8.42492 19.4792 8.42492C21.1707 8.42492 21.9583 10.2793 21.9583 12.4539C21.9583 13.5377 21.522 14.4358 21.522 14.4358C21.6977 14.5449 21.9867 14.892 21.9144 15.5082C21.7784 16.6572 21.3194 16.9476 21.0262 16.9731C20.9142 18.0441 19.8517 19.4154 19.4792 19.6152V21.4058C20.3051 24.0933 25.2634 22.1581 26.9167 27.1164C26.9167 27.1164 23.2078 31.1667 17 31.1667C10.7567 31.1667 7.08333 27.1164 7.08333 27.1164Z"
                          fill="#352E73"
                        />
                      </InputAdornmentSVGWrapper>
                    </InputAdornment>
                  ),
                }}
              />

              <FormFieldPassword
                id={'password'}
                label={'Password'}
                value={this.props.getState('password', '')}
                onChange={(e) => handleTextAreaChanges(e, this)}
                onBlur={(e) => setFieldTouched(e, this)}
                error={!!formErrors.password && (touchedFields.has('password') || formValidated)}
                helperText={(touchedFields.has('password') || formValidated) && formErrors.password?.message}
              />

              <FormFieldPassword
                id={'passwordConfirm'}
                label="Confirm password"
                value={this.props.getState('passwordConfirm', '')}
                onChange={(e) => handlePasswordConfirmationChanges(e, this, this.props.state.password as string)}
                onBlur={(e) => setFieldTouched(e, this)}
                error={!!formErrors.passwordConfirm && (touchedFields.has('passwordConfirm') || formValidated)}
                helperText={
                  (touchedFields.has('passwordConfirm') || formValidated) && formErrors.passwordConfirm?.message
                }
              />

              <TextField
                required
                id="company_name"
                type="text"
                placeholder="Company name"
                variant="outlined"
                value={this.props.getState('company_name', '')}
                onChange={(e) => handleTextAreaChanges(e, this)}
                onBlur={(e) => setFieldTouched(e, this)}
                error={!!formErrors.company_name && (touchedFields.has('company_name') || formValidated)}
                helperText={(touchedFields.has('company_name') || formValidated) && formErrors.company_name?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InputAdornmentSVGWrapper>
                        <svg x="7" y="8">
                          <path
                            d="M7.35 0V1H0V9C0 10.105 0.93975 11 2.1 11H18.9C20.0602 11 21 10.105 21 9V1H13.65V0H7.35ZM10.5 7C11.0796 7 11.55 7.448 11.55 8C11.55 8.552 11.0796 9 10.5 9C9.9204 9 9.45 8.552 9.45 8C9.45 7.448 9.9204 7 10.5 7ZM0 12.4434V17H21V12.4434C20.3794 12.7874 19.6686 13 18.9 13H2.1C1.3314 13 0.62055 12.7874 0 12.4434Z"
                            fill="#352E73"
                          />
                        </svg>
                      </InputAdornmentSVGWrapper>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl required component="fieldset" variant="standard" error={!!formErrors.is_eula_accepted}>
                <FormLabel>
                  <Checkbox
                    id="is_eula_accepted"
                    onChange={(e) => handleCheckboxChanges(e, this)}
                    checked={this.props.getState('is_eula_accepted', false)}
                  />
                  <span>I agree to the </span>
                  <Link href={config.links.termsOfUse} target={'_blank'}>
                    Terms of Service
                  </Link>
                  <span> and </span>
                  <Link href={config.links.privatePolicy} target={'_blank'}>
                    Privacy Policy
                  </Link>
                </FormLabel>
                <FormHelperText>{formErrors.is_eula_accepted?.message}</FormHelperText>
              </FormControl>
            </Box>
          </Grid>
          <Grid item md={5} sx={{ mb: 3, display: { xs: 'none', sm: 'none', md: 'block' } }}>
            <img src={stepIcon} alt="Step profile" width={285} height={312} />
          </Grid>
        </Grid>
        <Grid item>
          {Navigation({
            props: this.props,
            component: this,
            validationSchema: this.validationSchema,
            nextCallback: this.onNext,
          })}
        </Grid>
      </Grid>
    )
  }

  onNext = () => {
    const { company_name, full_name } = this.props.state || {}
    analytics.sendEvent('Onboard@ProfileProvided', {}, { company_name, full_name })

    return false // important
  }

  passwordFiledValidation = Joi.string()
    .pattern(new RegExp('^(?=.*[a-zA-Z])(?=.*\\d)[a-zA-Z\\d#?!@$%^&*-]{8,30}$'))
    .required()
    .label('Password')
    .messages({
      'string.pattern.base': `{{#label}} should have minimum 8 and maximum 30 characters, one letter and one number`,
    })

  validationSchema = Joi.object({
    full_name: Joi.string().min(3).max(60).required().label('Full name'),
    password: this.passwordFiledValidation,
    passwordConfirm: Joi.any()
      .equal(Joi.ref('password'))
      .required()
      .label('Confirm password')
      .messages({ 'any.only': 'Password does not match' }),
    company_name: Joi.string().min(3).max(60).required().label('Company name'),
    is_eula_accepted: Joi.boolean().disallow(false).required().messages({
      'any.invalid': `You must agree to continue`,
      'any.required': `You must agree to continue`,
    }),
  })
    .options({
      abortEarly: true,
    })
    .pattern(/.+/, Joi.allow())

  // Separate schema is needed for immediate password match validation.
  validationPasswordSchema = Joi.object({
    password: this.passwordFiledValidation,
    passwordConfirm: Joi.any()
      .equal(Joi.ref('password'))
      .required()
      .label('Confirm password')
      .messages({ 'any.only': 'Password does not match' }),
  })

  componentDidMount() {
    analytics.sendEvent('page@StepProfile')
    this.props.setState('is_eula_accepted', true)
  }
}

export default StepProfile
